import React, { useEffect, useRef, useState } from 'react';
import { handleAddText } from '../../gridpagefunction/Text';
import { handleAddDefaultHotspot } from '../../gridpagefunction/HotSpot';
import { updateOpenModals } from '../../../redux/Slice/gridSlice';
import store from '../../../redux/store';
import { setLoading } from '../../../redux/Slice/globalStates';
import { iconsPath } from '../../../utils/imagePath';
import {
  Elements,
  Light,
  MenuItems,
  Shapes,
} from '../../GridAssets/GridAssetsData';
import useClickOutside from '../../UseClickOutside';
import { useDispatch } from 'react-redux';
import { AutomationSystem } from '../../../utils/automation';
import { CameraZoom, SubMenuPortal } from '../../../utils/gridUtils';
import {
  handleClouds,
  handleFire,
  handleFoliage,
  handleRain,
  handleSnow,
  handleStars,
  handleTerrain,
  handleWater,
} from '../../../utils/Elements';
import { addParticle } from '../../../redux/Slice/particleSlice';
import { HandleShapeClick } from '../../GridAssets/AssetsLibrary';
import { addLight } from '../../../redux/Slice/gridSlice';
import { LightInitialData, getLightInitialData } from '../../../utils/constant';
import { selectObjectInScene } from '../../../pages/Dashboard/GridPage';
import { scheduleSelectLightInScene } from '../../../utils/ThreeJsUtils/lightHelperUtils';
import {
  updateActiveComponent,
  setSelectedLightId,
} from '../../../redux/Slice/globalStates';
import { switchCamera } from '../../gridpagefunction/Camera';

const AppTopNew = React.memo(
  ({
    objectt,
    activeTab,
    submenuOpen,
    setActiveTab,
    setSubmenuOpen,
    experienceId,
    setLeftPanelKey,
    leftPanelKey,
  }) => {
    const { scene, transformControls } = objectt;
    const dispatch = useDispatch();

    // Initialize automation system
    const [automationSystem] = useState(() => new AutomationSystem());
    const [tooltipId, setTooltipId] = useState(null);
    const [showSearchMode, setShowSearchMode] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const searchInputRef = useRef(null);
    const [searchLoading, setSearchLoading] = useState(false);

    const buttonsConfig = [
      {
        id: 'gltf',
        label: 'New Object',
        imgSrc: iconsPath?.group10Image,
        menuItem: MenuItems,
      },
      {
        id: 'search',
        label: 'Search',
        imgSrc: iconsPath?.searchImage || iconsPath?.group14Image,
        onClick: () => {
          // Measure container width before switching to search mode
          if (menuContainerRef.current) {
            const width = menuContainerRef.current.offsetWidth;
            if (width > 0) {
              setContainerWidth(width);
            }
          }
          setShowSearchMode(true);
          setActiveTab('');
          setSubmenuOpen('');
          setTooltipId(null);
        },
      },
      {
        id: 'shapes',
        label: 'Shapes',
        imgSrc: iconsPath?.group13Image,
        menuItem: Shapes,
      },
      {
        id: 'elements',
        label: 'Elements',
        imgSrc: iconsPath?.group16Image,
        menuItem: Elements,
      },
      {
        id: 'text',
        label: 'Text',
        imgSrc: iconsPath?.group14Image,
        onClick: () => {
          handleAddText(objectt);
          store.dispatch(updateOpenModals({ name: 'text', isOpen: true }));
        },
      },
      {
        id: 'lights',
        label: 'Lights',
        imgSrc: iconsPath?.group15Image,
        menuItem: Light,
      },
    ];

    const modelRef = useRef(null);
    useClickOutside(modelRef, () => {
      if (!showSearchMode) {
        closeTopSection();
      }
    });

    const handleElementEffect = (effect) => {
      const existing = scene.getObjectByName(effect.toLowerCase());
      if (existing) return scene.remove(existing);

      const map = {
        terrain: handleTerrain,
        water: handleWater,
        clouds: handleClouds,
        stars: handleStars,
        rain: handleRain,
        snow: handleSnow,
        fire: handleFire,
        foliage: handleFoliage,
      };

      const fn = map[effect.toLowerCase()];
      if (fn) {
        const particleId = `${effect.toLowerCase()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const particleData = {
          id: particleId,
          type: effect.toLowerCase(),
          name: effect.toLowerCase(),
          ...(effect.toLowerCase() === 'clouds'
            ? { particleCount: 20, range: 20 }
            : {}),
        };

        fn(scene, particleData);

        const obj = scene.children[scene.children.length - 1];
        if (obj && obj.deleteType === 'particle') {
          const isCloud = effect.toLowerCase() === 'clouds';
          dispatch(
            addParticle({
              id: particleId,
              // ═══════ FIX: Remove explicit name and set isDuplicate to allow Redux name incrementing ═══════
              // Let the Redux reducer generate the name with proper incrementing (Fire, Fire 1, Fire 2, etc.)
              // Set isDuplicate: true so Redux will check for existing particles and increment names
              type: effect.toLowerCase(),
              particleCount: isCloud
                ? obj.userData?.particleCount ?? 20
                : 1000,
              ...(isCloud
                ? {
                    range: obj.userData?.range ?? 20,
                    life: obj.userData?.life ?? 0,
                    color: obj.userData?.color ?? '#ffffff',
                  }
                : {}),
              isDuplicate: true, // Enables name incrementing in Redux reducer
            })
          );
        }
      }
    };

    const handleLightAddition = (lightType = 'Directional Light') => {
      const newId = 'light-' + Math.random().toString(36).substr(2, 9);
      const newLight = {
        ...getLightInitialData(lightType),
        id: newId,
        light_type: lightType,
        name: lightType,
        temp_file_name: newId,
      };
      store.dispatch(addLight(newLight));
      store.dispatch(setSelectedLightId(newId));
      store.dispatch(updateActiveComponent({ type: 'add', option: 'Light' }));
      scheduleSelectLightInScene(scene, newId, selectObjectInScene);
    };

    const closeTopSection = () => {
      setActiveTab('');
      setSubmenuOpen('');
      setTooltipId(null);
    };

    const handleSearchInputChange = (e) => {
      setSearchValue(e.target.value);
    };

    const handleSearchEscape = () => {
      setShowSearchMode(false);
      setSearchValue('');
      setSearchLoading(false);
    };

    const handleSearchSubmit = async (e) => {
      // Handle both event and non-event calls
      if (e && e.preventDefault) {
        e.preventDefault();
      }

      if (!searchValue.trim()) return;
      setSearchLoading(true);
      store.dispatch(setLoading(true));

      try {
        await automationSystem.processCommand(
          searchValue,
          scene,
          transformControls,
          store
        );
      } catch (error) {
        console.error('Search command error:', error);
      } finally {
        setSearchLoading(false);
        store.dispatch(setLoading(false));
        setSearchValue('');
      }
    };

    const handleMenuClick = (action, type) => {
      if (
        action === 'shapes' ||
        action === 'elements' ||
        action === 'Light' ||
        action === 'Cemera'
      ) {
        setSubmenuOpen(action);
        setTooltipId(null);
        return;
      } else if (type === 'Upload/Import') {
        store.dispatch(updateOpenModals({ name: 'upload', isOpen: true }));
      } else if (type === 'Library') {
        store.dispatch(updateOpenModals({ name: 'library', isOpen: true }));
      } else if (action === 'text' || type === 'Text') {
        handleAddText(objectt);
        store.dispatch(updateOpenModals({ name: 'text', isOpen: true }));
      } else if (type === 'Hotspots') {
        handleAddDefaultHotspot(objectt);
        store.dispatch(
          updateActiveComponent({ option: 'HotSpot', type: 'add' })
        );
      }
      setTooltipId(null);
    };

    const handleSubMenuClick = (action, sublabel, type) => {
      if (action === 'shapes') {
        HandleShapeClick({ action: sublabel, scene, transformControls });
      } else if (action === 'elements') {
        handleElementEffect(sublabel);
      } else if (action === 'Cemera') {
        switchCamera(objectt, sublabel?.toLowerCase());
      } else if (action === 'Light' || action === 'lights') {
        const lightTypeMap = {
          Directional: 'Directional Light',
          Point: 'Point Light',
          Spot: 'Spot Light',
          Area: 'Area Light',
          Ambient: 'Ambient Light',
        };
        const lightType = lightTypeMap[sublabel] || 'Directional Light';
        handleLightAddition(lightType);
      }
      closeTopSection();
    };

    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
    const menuContainerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(null);

    // Measure and preserve container width to prevent layout shifts
    useEffect(() => {
      if (!showSearchMode && menuContainerRef.current) {
        const width = menuContainerRef.current.offsetWidth;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    }, [showSearchMode]);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (
          menuContainerRef.current &&
          !menuContainerRef.current.contains(e.target)
        ) {
          if (!showSearchMode) {
            setSubmenuOpen(null);
            setTooltipId(null);
          }
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, [showSearchMode]);

    // Handle escape key for search mode
    useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape' && showSearchMode) {
          handleSearchEscape();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showSearchMode]);

    // Single container that stays the same - conditionally render content inside
    return (
      <>
        <div className="flex justify-center items-center w-fit relative bottom-[28px]">
          <div
            className="bg-[#131418] top_section_new rounded-[5px]"
            ref={menuContainerRef}
            style={{
              width:
                showSearchMode && containerWidth
                  ? `${containerWidth}px`
                  : 'fit-content',
              minWidth: showSearchMode && !containerWidth ? '402px' : 'auto',
            }}
          >
            {showSearchMode ? (
              // Search mode - render search input in same container
              <div className="flex items-center w-full px-2 gap-3 h-[34px]">
                {/* Search Icon */}
                <div className="flex-shrink-0 text-[#9E9D9D]">
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </div>

                {/* Search Input */}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSearchSubmit();
                    }
                  }}
                  placeholder="Search or describe what you want to add..."
                  className="flex-1 bg-transparent text-sm text-white h-[34px] focus:outline-none placeholder:text-[#9E9D9D]"
                  disabled={searchLoading}
                  autoFocus
                />

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {searchValue.trim() && (
                    <button
                      onClick={handleSearchSubmit}
                      disabled={searchLoading}
                      className={`text-white hover:text-[#4CAF50] transition-colors ${searchLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      title="Submit"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m22 2-7 20-4-9-9-4 20-7z" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={handleSearchEscape}
                    className="text-[#9E9D9D] hover:text-white transition-colors"
                    title="Close (Esc)"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              // Normal menu view - render buttons in same container
              <>
                {buttonsConfig.map((btn) => (
                  <button
                    key={btn.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (btn.id === 'search') {
                        btn.onClick?.();
                      } else {
                        setActiveTab(btn.id);
                        setTooltipId(null);
                        btn.onClick?.();
                      }
                    }}
                    onMouseEnter={() => {
                      if (!activeTab) setTooltipId(btn.id);
                    }}
                    onMouseLeave={() => setTooltipId(null)}
                    className={`top-icon-button group relative ${btn.menuItem && activeTab === btn.id ? 'active' : ''
                      }`}
                  >
                    <img
                      draggable={false}
                      className={`top-icon-img ${btn.id === 'search' ? '!w-7 !h-7' : ''}`}
                      src={btn.imgSrc}
                      alt={btn.label}
                    />
                    {tooltipId === btn.id && !activeTab && (
                      <span className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-[#1e1f22] text-white text-xs rounded px-2 py-1 opacity-100 transition-opacity z-50 whitespace-nowrap">
                        {btn.label}
                      </span>
                    )}
                    {btn.id === activeTab && btn.menuItem?.length > 0 && (
                      <div className="absolute w-[180px] left-0 pt-2 top-[99%] z-[9999]">
                        <div className="w-full bg-[#131418] rounded-[5px]">
                          <ul className="py-[6px] w-full max-h-[calc(100vh-100px)] overflow-y-auto scrollbar-none">
                            {btn.menuItem.map((item, idx) =>
                              item.gap ? (
                                <div
                                  key={idx}
                                  className="border-b border-[#2B2B2B] my-1"
                                />
                              ) : item.heading ? (
                                <div
                                  key={idx}
                                  className="text-[#D9D9D9] font-semibold mx-2 my-1 text-left"
                                >
                                  {item.heading}
                                </div>
                              ) : (
                                <li
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTooltipId(null);
                                    if (item.submenu?.length) {
                                      const rect =
                                        e.currentTarget.getBoundingClientRect();
                                      setSubmenuPosition({
                                        top: rect.top,
                                        left: rect.right + 5,
                                      });
                                      setSubmenuOpen(item.action);
                                    } else {
                                      item.onClick?.();
                                      handleMenuClick(btn.id, item.label);
                                      handleSubMenuClick(btn.id, item.label);
                                      setSubmenuOpen(null);
                                    }
                                  }}
                                  className="relative flex items-center px-4 py-[6px] gap-2 cursor-pointer hover:bg-[#37383F]"
                                >
                                  <img
                                    src={item.icon}
                                    alt={item.label}
                                    className={item.imgClass || "w-4 h-4"}
                                  />
                                  <span className="text-[#D9D9D9] text-[14px]">
                                    {item.label}
                                  </span>
                                  {item.submenu?.length > 0 && (
                                    <img
                                      src={iconsPath.backarrowImage}
                                      alt="submenu"
                                      className={`ml-auto w-3 h-3 transition-transform ${submenuOpen === item.action
                                          ? 'rotate-90'
                                          : ''
                                        }`}
                                    />
                                  )}
                                  {item.submenu?.length > 0 &&
                                    submenuOpen === item.action && (
                                      <SubMenuPortal position={submenuPosition}>
                                        <div className="bg-[#131418] rounded-[5px] w-[180px] max-h-[calc(100vh-170px)] overflow-y-auto scrollbar-none">
                                          <ul className="py-[6px] w-full">
                                            {item.submenu.map((sub, sIdx) =>
                                              sub.gap ? (
                                                <div
                                                  key={sIdx}
                                                  className="border-b border-[#2B2B2B] my-1"
                                                />
                                              ) : sub.heading ? (
                                                <div
                                                  key={sIdx}
                                                  className="text-[#D9D9D9] font-semibold mx-2 my-1 text-left"
                                                >
                                                  {sub.heading}
                                                </div>
                                              ) : (
                                                <li
                                                  key={sIdx}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setTooltipId(null);
                                                    sub.onClick?.();
                                                    handleSubMenuClick(
                                                      item.action,
                                                      sub.label,
                                                      'sub'
                                                    );
                                                    requestAnimationFrame(
                                                      () => {
                                                        setSubmenuOpen(null);
                                                      }
                                                    );
                                                  }}
                                                  className="flex items-center px-4 py-[6px] gap-2 cursor-pointer hover:bg-[#37383F]"
                                                >
                                                  <img
                                                    src={sub.icon}
                                                    alt={sub.label}
                                                    className={sub.imgClass || "w-4 h-4"}
                                                  />
                                                  <span className="text-[#D9D9D9] text-[14px]">
                                                    {sub.label}
                                                  </span>
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        </div>
                                      </SubMenuPortal>
                                    )}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </button>
                ))}
                <button className="line-img">
                  <img
                    style={{ height: '35px' }}
                    src={iconsPath?.line6Image}
                    alt="line"
                  />
                </button>
                <CameraZoom objectt={objectt} />
              </>
            )}
          </div>
        </div>
      </>
    );
  }
);

export default AppTopNew;
